import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { assessmentService, userService } from "../../../../api/services";
import makeRequest, { makeThirdPartyRequest } from "../../../../api";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";

const MAX_COUNT = 5;

const AddReportPage = () => {
  const params = useParams();
  const userId = params.userId;
  const navigate = useNavigate();
  const [inputData, setInputData] = useState({
    medicalReports: [],
    reportGeneratedAt: "",
    healthIssueType: "",
    documentType: "",
    initiationPoint: "COACH_PANEL",
  });
  const subscription = useLocation().state;

  const [issueTypes, setIssueTypes] = useState<any>([]);
  const [fileLimit, setFileLimit] = useState(false);
  const [issueTypeMap, setIssueTypeMap] = useState<{ [key: string]: any }>({});
  const [fileTypeOption, setFileTypeOptions] = useState([]);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchIssueTypes = async () => {
    try {
      const res = await makeRequest(
        assessmentService.getHealthMetricsOptions()
      );
      setIssueTypes([...res?.data?.body?.issueBasedOptions]);
    } catch (error: any) {
      toast.error("Error fetching health metrics options!");
    }
  };

  useEffect(() => {
    fetchIssueTypes();
  }, []);

  const getUserData = async () => {
    try {
      const res = await makeRequest(userService.getUserDetails(userId));
      setUserDetails({ ...res?.data?.userDto });
    } catch (error: any) {
      toast.error("Error fetching user details!");
    }
  };

  useEffect(() => {
    if (userId) {
      getUserData();
    }
  }, [userId]);

  useEffect(() => {
    if (issueTypes.length) {
      let issueMap = {};
      issueTypes?.map((item: any) => {
        issueMap = {
          ...issueMap,
          [item?.healthIssueType]: {
            options: item?.options,
            displayName: item?.displayName,
          },
        };
      });
      setIssueTypeMap({ ...issueMap });
    }
  }, [issueTypes]);

  useEffect(() => {
    if (inputData?.healthIssueType !== "") {
      const issueKey = inputData?.healthIssueType as keyof typeof issueTypeMap;
      setFileTypeOptions(issueTypeMap[issueKey]?.options?.documentType);
    }
  }, [inputData]);

  const handleUploadFiles = (files: any) => {
    const uploaded: any = [];
    let limitExceeded = false;
    files.some((file: any) => {
      if (uploaded.findIndex((f: any) => f.name === file.name) === -1) {
        uploaded.push(file);
        if (uploaded.length === MAX_COUNT) {
          setFileLimit(true);
        }
        if (uploaded.length > MAX_COUNT) {
          alert("You can only add a maximum of " + MAX_COUNT + " files");
          setFileLimit(false);
          limitExceeded = true;
          return true;
        }
      }
    });
    if (!limitExceeded) {
      setInputData({
        ...inputData,
        medicalReports: uploaded,
      });
    }
  };

  const handleFileEvent = (e: any) => {
    const chosenFile = Array.prototype.slice.call(e.target.files);
    handleUploadFiles(chosenFile);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "reportGeneratedAt") {
      const date = new Date(value);
      setInputData({ ...inputData, [name]: moment(date).valueOf().toString() });
    } else {
      setInputData({ ...inputData, [name]: value });
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const reportsMediaUrls: string[] = [];
    setIsLoading(true);
    if (inputData.medicalReports?.length) {
      const date = moment();

      const presignedUrlsResponse = [];
      for (const file of inputData?.medicalReports) {
        const fileData: any = file;
        const lastDotIndex = fileData?.name?.lastIndexOf(".");
        const fileExtension =
          lastDotIndex !== -1 ? fileData?.name?.slice(lastDotIndex + 1) : "";
        const uuid = uuidv4();
        const issueType = inputData?.healthIssueType;
        const fileName = `medical-reports/${date.format("YYYY")}/${date.format(
          "MM"
        )}/${date.format(
          "DD"
        )}/${userId}/${issueType}/${uuid}.${fileExtension}`;

        const response = await makeRequest(
          assessmentService.getPreSignedUrlForReports(fileName)
        );
        presignedUrlsResponse.push(response);
      }
      const promisesToResolve = presignedUrlsResponse.reduce(
        (acc: Array<Promise<any>>, response, index) => {
          const file: any = inputData.medicalReports?.[index];
          if (response && file) {
            return acc.concat(
              makeThirdPartyRequest({
                url: response?.data?.body?.signedUrl,
                method: "PUT",
                data: file,
                headers: {
                  "Content-Type": file?.type,
                },
              })
            );
          }
          return acc;
        },
        []
      );

      const response = (await Promise.allSettled(promisesToResolve)) as Array<
        PromiseSettledResult<AxiosResponse<any, any>>
      >;
      response.forEach((itr) => {
        if (itr.status === "fulfilled") {
          const bucketURL: string =
            process.env.REACT_APP_REPORT_S3_BUCKET_URL || "";
          const cdnURL: string =
            process.env.REACT_APP_REPORT_CDN_BASE_URL || "";

          const mediaPath = itr?.value?.config?.url
            ?.split?.("?")?.[0]
            ?.replaceAll(bucketURL, cdnURL);

          if (mediaPath) {
            reportsMediaUrls.push(mediaPath);
          }
        }
      });
    }

    const formResponse = {
      healthIssueType: inputData.healthIssueType,
      documentType: inputData.documentType,
      initiationPoint: inputData.initiationPoint,
      reportGeneratedAt: inputData.reportGeneratedAt,
      fileDetailsList: inputData.medicalReports?.map(
        (item: any, index: number) => {
          const reportId = reportsMediaUrls?.[index]
            ?.split("/")
            ?.reverse()?.[0]
            ?.split(".")?.[0];

          return {
            s3URL: reportsMediaUrls?.[index],
            contentType: item?.type,
            fileName: item?.name,
            reportId,
          };
        }
      ),
    };

    try {
      const res = await makeRequest(
        assessmentService.createUserReport(formResponse, userId)
      );
      toast.success("Report added successfully.");
      navigate(`/users/${userId}/reports`);
      setIsLoading(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  return (
    <div className="container-lg">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-2">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/users/${userId}/reports`} state={subscription}>
              Reports
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Add report
          </li>
        </ol>
      </nav>
      {userDetails?.fullName && (
        <h6 className="fw-700 fs-24 mb-0">
          Add report for {userDetails?.fullName}
        </h6>
      )}
      <div className="w-50">
        <form className="mt-2" onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <label htmlFor="reportFiles" className="form-label">
              <strong>Upload reports</strong>
            </label>
            <input
              className="form-control"
              type="file"
              id="medicalReports"
              name="medicalReports"
              multiple
              accept="image/jpeg,image/png,application/pdf"
              disabled={fileLimit}
              onChange={handleFileEvent}
              required
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="generation-date" className="form-label">
              <strong>Report time</strong>
            </label>
            <input
              className="form-control"
              type="datetime-local"
              id="generation-date"
              name="reportGeneratedAt"
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="issue-type" className="form-label">
              <strong>Health issue type</strong>
            </label>
            <select
              id="issue-type"
              className="form-select"
              aria-label="Default select example"
              defaultValue=""
              name="healthIssueType"
              onChange={handleChange}
              required
            >
              <option value="">Select issue type</option>
              {Object.keys(issueTypeMap)?.map((item: any, index: number) => {
                const issueKey = item as keyof typeof issueTypeMap;
                return (
                  <option value={issueKey} key={`issue type option ${index}`}>
                    {issueTypeMap[issueKey]?.displayName}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="form-group mb-3">
            <label htmlFor="document-type" className="form-label">
              <strong>File type</strong>
            </label>
            <select
              id="document-type"
              className="form-select"
              aria-label="Default select example"
              defaultValue=""
              name="documentType"
              onChange={handleChange}
              required
            >
              <option value="">Select file type</option>
              {fileTypeOption?.map((item: any, index: number) => {
                return (
                  <option
                    value={item?.value}
                    key={`issue type option ${index}`}
                  >
                    {item?.displayName}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="form-group mb-3">
            <button
              type="submit"
              className="btn btn-outline-success px-4"
              disabled={isLoading}
            >
              Add report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddReportPage;
